// components/Layout/Header.tsx
import { useState, useRef, type ReactNode } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useNavigate } from 'react-router-dom';
import {
  User,
  ChevronDown,
  MessageSquare,
  X,
  Menu,
  Settings,
  Printer,
  LogOut,
  Info,
  Shield,
  Terminal,
  UserPlus,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { useMobile } from '../../contexts/MobileContext';
import { BrandMark, controlClasses } from '../ui';
import { FeedbackModal, ConfirmModal } from '../shared/modals';
import { useConfirm } from '../../contexts/ConfirmContext';
import { getInitials } from '../../utils/userUtils';
import { SchoolSwitcher } from '../SchoolSwitcher';

export interface HeaderStudentProfile {
  id?: string | number;
  name?: string;
  email?: string;
  degree?: string;
  year?: string;
}

export interface HeaderProps {
  studentProfile?: HeaderStudentProfile | Record<string, unknown> | null;
  planSwitcher?: ReactNode;
  onOpenCommandCenter?: () => void;
  /** Active plan id, passed to FeedbackModal so a report says which plan it is about. */
  planId?: number | null;
}

const Header = ({
  studentProfile,
  planSwitcher,
  onOpenCommandCenter,
  planId,
}: HeaderProps = {}) => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { isAdmin } = useApp();
  const { isMobileMenuOpen, toggleMobileMenu, closeMobileMenu, isMobile } = useMobile();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { info } = useConfirm();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  useEscapeKey(() => {
    setIsDropdownOpen(false);
    closeMobileMenu();
  });

  useClickOutside(mobileMenuRef, closeMobileMenu, isMobileMenuOpen, { includeTouch: true });

  const handleSignOut = () => {
    setShowSignOutModal(true);
  };

  const confirmSignOut = async () => {
    setShowSignOutModal(false);
    await signOut();
  };

  const handleAboutClick = () => {
    setIsDropdownOpen(false);
    info({
      title: 'Important Notice',
      content: (
        <div className="space-y-4 text-sm text-gray-600">
          <p>
            This is an unofficial planning tool. It is not affiliated with or endorsed by any university.
          </p>
          <div>
            <p className="font-medium text-gray-700 mb-2">Always verify your course selections with:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Your academic advisor</li>
              <li>Official registration system</li>
              <li>University course catalog</li>
            </ul>
          </div>
          <p className="text-gray-500 italic">
            Use at your own risk. No warranty or guarantee of accuracy.
          </p>
        </div>
      ),
    });
  };

  const getUserInitials = (): string => {
    const metadataName = (user?.user_metadata as { name?: string } | undefined)?.name;
    if (metadataName) {
      return getInitials(metadataName);
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const userMetadataName = (user?.user_metadata as { name?: string } | undefined)?.name;

  return (
    <header className="bg-surface-card shadow-sm border-b border-line-soft safe-area-top">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <div className="flex items-center gap-2.5">
              {/* No containing tile: the mark's hole is its identity, and a
                  filled square behind a hollow square cancels it out. The mark
                  carries the institution color itself. Decorative — the
                  "Planner" heading beside it is the accessible name. */}
              <BrandMark size={28} />
              <h1 className="text-lg font-bold text-ink tracking-tight">Planner</h1>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Both switchers are md-and-up ONLY: below md they render in the
                mobile strip under the divider (see the `md:hidden` row below).
                Without these gates both mount at once on a phone and the strip
                reads as a duplicate of the header row — which is exactly what a
                reviewer reported ("plan dropdown has a duplicate, the line
                below the planner logo"). The rest of this row — avatar
                dropdown, hamburger — stays visible at every width. */}
            {user && (
              <div className="hidden md:block">
                <SchoolSwitcher />
              </div>
            )}

            {user && planSwitcher && (
              <div className="hidden md:flex items-center space-x-2">
                {planSwitcher}
              </div>
            )}

            {/* Feedback lived two clicks deep in the account dropdown, which is
                adequate for occasional beta feedback and weak for people actively
                hunting bugs. Kept there too — it costs nothing and some people look.
                A header button rather than a floating one: a FAB would need
                state-dependent offsets to dodge ChatMinimizedPill (bottom-6 right-6
                when the assistant is off) and MobileTabBar.

                Styled as a tinted-primary pill rather than a ghost button so it
                reads as an invitation, not chrome — the same grammar SchoolSwitcher
                uses one slot to the left, which single-membership students never
                see, so for them this is the one colored control in the header.
                Solid `primary` was rejected: the header carries no CTA and a brand
                red block would read as an alert at UMD. The label shows from `sm`
                (was `lg`) so tablets get the word, not just the icon. */}
            {user && (
              <button
                onClick={() => setShowFeedbackModal(true)}
                className={controlClasses(
                  'md',
                  'gap-1.5 px-2.5 sm:px-3 text-sm font-semibold',
                  'bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20'
                )}
                title="Share feedback"
                aria-label="Share feedback"
              >
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
                <span className="hidden sm:inline">Feedback</span>
              </button>
            )}

            {user ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={controlClasses(
                    'md',
                    'gap-2 px-2 text-sm text-ink hover:bg-surface-sunken'
                  )}
                >
                  <div className="h-8 w-8 bg-primary text-white rounded-full flex items-center justify-center font-medium">
                    {getUserInitials()}
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-surface-card rounded-card shadow-lg border border-line-soft py-1 z-50">
                    <div className="px-4 py-3 border-b border-line-soft">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-primary text-white rounded-full flex items-center justify-center font-medium">
                          {getUserInitials()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {userMetadataName || (studentProfile as { name?: string } | null)?.name || 'Student'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="py-1 border-b border-line-soft">
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate('/settings?tab=invites');
                        }}
                        className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left flex items-center gap-2"
                      >
                        <UserPlus className="h-4 w-4 text-ink-3" />
                        <span>Invite Friends</span>
                      </button>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          setShowFeedbackModal(true);
                        }}
                        className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left flex items-center gap-2"
                      >
                        <MessageSquare className="h-4 w-4 text-ink-3" />
                        <span>Beta Feedback</span>
                      </button>
                    </div>

                    <div className="py-1 border-b border-line-soft">
                      <button
                        onClick={handleAboutClick}
                        className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left"
                      >
                        About
                      </button>
                    </div>

                    {isAdmin && (
                      <div className="py-1 border-b border-line-soft">
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            navigate('/admin');
                          }}
                          className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left"
                        >
                          Admin Dashboard
                        </button>
                      </div>
                    )}

                    {isAdmin && onOpenCommandCenter && (
                      <div className="py-1 border-b border-line-soft">
                        <button
                          onClick={() => {
                            setIsDropdownOpen(false);
                            onOpenCommandCenter();
                          }}
                          className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left flex items-center justify-between"
                        >
                          <span>Command Center</span>
                          <span className="text-xs text-gray-400">⌘K</span>
                        </button>
                      </div>
                    )}

                    <div className="py-1 border-b border-line-soft">
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          navigate('/settings');
                        }}
                        className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left"
                      >
                        Profile Settings
                      </button>
                    </div>

                    <div className="py-1 border-b border-line-soft">
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          window.print();
                        }}
                        className="w-full px-4 py-2 text-sm text-ink hover:bg-surface-sunken text-left flex items-center justify-between"
                      >
                        <span>Print Plan</span>
                        <span className="text-xs text-gray-400">⌘P</span>
                      </button>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false);
                          handleSignOut();
                        }}
                        className="w-full px-4 py-2 text-sm text-status-critical hover:bg-status-critical/10 text-left"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <div className="flex items-center text-sm text-gray-500">
                  <User className="h-4 w-4 mr-1" />
                  <span>Guest Mode</span>
                </div>
                <div className="text-xs text-gray-400 hidden sm:block">
                  Sign in to save your plan
                </div>
              </div>
            )}

            <button
              className="md:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {!isMobileMenuOpen && (
          <div className="md:hidden border-t border-line-soft py-2 flex items-center gap-2">
            {user && <SchoolSwitcher />}
            {user && planSwitcher && (
              <div className="flex-1">
                {planSwitcher}
              </div>
            )}
          </div>
        )}
      </div>

      {isMobileMenuOpen && isMobile && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />

          <div
            ref={mobileMenuRef}
            className="fixed top-0 right-0 h-full w-72 max-w-[80vw] bg-surface-card shadow-xl z-50 md:hidden transform transition-transform duration-200 ease-out"
            role="dialog"
            aria-modal="true"
            aria-label="Mobile menu"
          >
            <div className="flex items-center justify-between p-4 border-b border-line-soft">
              <span className="text-lg font-semibold text-ink">Menu</span>
              <button
                onClick={closeMobileMenu}
                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {user && (
              <div className="p-4 border-b border-line-soft bg-surface-sunken/60">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-primary text-white rounded-full flex items-center justify-center font-medium flex-shrink-0">
                    {getUserInitials()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {userMetadataName || (studentProfile as { name?: string } | null)?.name || 'Student'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                </div>
              </div>
            )}

            <nav className="flex-1 overflow-y-auto py-2">
              {user && planSwitcher && (
                <div className="px-4 py-3 border-b border-line-soft">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Active Plan</p>
                  {planSwitcher}
                </div>
              )}

              {user && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/settings?tab=invites');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
                >
                  <UserPlus className="h-5 w-5 text-emerald-500" />
                  <span>Invite Friends</span>
                </button>
              )}

              {user && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    setShowFeedbackModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
                >
                  <MessageSquare className="h-5 w-5 text-amber-500" />
                  <span>Beta Feedback</span>
                </button>
              )}

              <button
                onClick={() => {
                  closeMobileMenu();
                  handleAboutClick();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
              >
                <Info className="h-5 w-5 text-gray-400" />
                <span>About</span>
              </button>

              {isAdmin && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/admin');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
                >
                  <Shield className="h-5 w-5 text-gray-400" />
                  <span>Admin Dashboard</span>
                </button>
              )}

              {isAdmin && onOpenCommandCenter && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    onOpenCommandCenter();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
                >
                  <Terminal className="h-5 w-5 text-gray-400" />
                  <span>Command Center</span>
                </button>
              )}

              {user && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
                >
                  <Settings className="h-5 w-5 text-gray-400" />
                  <span>Profile Settings</span>
                </button>
              )}

              <button
                onClick={() => {
                  closeMobileMenu();
                  window.print();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-ink hover:bg-surface-sunken min-h-[44px]"
              >
                <Printer className="h-5 w-5 text-gray-400" />
                <span>Print Plan</span>
              </button>

              {user && <div className="my-2 border-t border-gray-100" />}

              {user && (
                <button
                  onClick={() => {
                    closeMobileMenu();
                    handleSignOut();
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-status-critical hover:bg-status-critical/10 min-h-[44px]"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign Out</span>
                </button>
              )}
            </nav>
          </div>
        </>
      )}

      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        planId={planId}
      />

      <ConfirmModal
        isOpen={showSignOutModal}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={confirmSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
      />
    </header>
  );
};

export default Header;
